import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Target, Trophy, TrendingUp, Hourglass } from 'lucide-react';
import { crore } from './api';
import { PaceAlerts, QuarterProgress, QuarterMatrix } from './PaceAlerts';

const Bar = ({ pct }) => (
  <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
    <div className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
);

const Cells = ({ r }) => (
  <>
    <td className="px-3 py-2 text-right tabular-nums">{crore(r.target)}</td>
    <td className="px-3 py-2 text-right tabular-nums text-green-700">{crore(r.achieved)}</td>
    <td className="px-3 py-2 w-40"><div className="flex items-center gap-2"><Bar pct={r.achievement_pct} /><span className="text-xs tabular-nums w-12 text-right">{r.achievement_pct}%</span></div></td>
    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{crore(r.pipeline)}</td>
    <td className="px-3 py-2 text-right tabular-nums text-red-600">{crore(r.gap)}</td>
  </>
);

const Kpi = ({ id, label, value, sub, icon: Icon }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`roadmap-kpi-${id}`}>
    <div className="flex items-center justify-between"><p className="text-xs font-medium text-slate-500 uppercase">{label}</p><Icon className="w-4 h-4 text-slate-400" /></div>
    <p className="text-2xl font-bold text-slate-900 mt-2">{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

const fmtKpi = (k) => (k.unit === 'amount' ? crore(k.target) : k.unit === 'percent' ? `${k.target}%` : k.target);
const fmtCur = (k) => (k.current == null ? null : k.unit === 'amount' ? crore(k.current) : k.current);

export const TargetOverview = ({ data }) => {
  const [open, setOpen] = useState({});
  const g = data.group;
  const toggle = (id) => setOpen({ ...open, [id]: !open[id] });
  return (
    <div className="space-y-6" data-testid="target-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi id="target" label={`${g.name} Target`} value={crore(g.target)} sub={`Domestic ${crore(g.domestic_target)} • Export ${crore(g.export_target)}`} icon={Target} />
        <Kpi id="won" label="Won (Sales)" value={crore(g.achieved)} sub={`Domestic ${crore(g.domestic_won)} • Export ${crore(g.export_won)}`} icon={Trophy} />
        <Kpi id="achievement" label="Achievement" value={`${g.achievement_pct}%`} sub={`Gap ${crore(g.gap)}`} icon={TrendingUp} />
        <Kpi id="pipeline" label="Open Pipeline" value={crore(g.pipeline)} sub="Draft + sent quotes" icon={Hourglass} />
        <Kpi id="expected" label="Expected by Today" value={crore(g.expected)} sub={`FY ${data.fy_progress_pct}% elapsed • pace ${g.pace_pct ?? '-'}%`} icon={Target} />
        <Kpi id="unallocated" label="Won, no BU" value={crore(g.unallocated_won)} sub="Tag enquiries with a BU" icon={AlertTriangle} />
      </div>

      <PaceAlerts alerts={data.alerts} fyProgress={data.fy_progress_pct} limit={6} expandable />
      <QuarterProgress row={g} title={`${g.name} – quarterly progress`} testId="group-quarters" />
      <QuarterMatrix companies={data.companies} />

      {data.mismatches.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="target-mismatches">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-900"><AlertTriangle className="w-4 h-4" /> Targets don't add up</p>
          <ul className="mt-2 space-y-1 text-sm text-amber-900">
            {data.mismatches.map((m, i) => (
              <li key={i} data-testid={`mismatch-${i}`}>• <b>{m.name}</b> target {crore(m.target)} vs {m.children} total {crore(m.sum_of_children)} ({m.sum_of_children > m.target ? 'over' : 'short'} by {crore(Math.abs(m.sum_of_children - m.target))})</li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="achievement-tree">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>{['Company / Business Unit / Salesperson', 'Target', 'Won', 'Achievement', 'Pipeline', 'Gap'].map((h, i) => <th key={h} className={`px-3 py-2 text-xs font-medium text-slate-500 uppercase ${i ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.companies.filter(c => c.active).map(c => (
              <React.Fragment key={c.id}>
                <tr className="bg-slate-900 text-white" data-testid={`ach-company-${c.id}`}>
                  <td className="px-3 py-2 font-semibold">{c.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{crore(c.target)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{crore(c.achieved)}</td>
                  <td className="px-3 py-2 text-right text-xs">{c.achievement_pct}%</td>
                  <td className="px-3 py-2 text-right tabular-nums">{crore(c.pipeline)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{crore(c.gap)}</td>
                </tr>
                {c.business_units.filter(b => b.active).map(b => (
                  <React.Fragment key={b.id}>
                    <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => toggle(b.id)} data-testid={`ach-bu-${b.id}`}>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1">{open[b.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <span className="font-medium text-slate-800">{b.name}</span>
                          {b.code && <span className="ml-1 text-[10px] font-mono bg-slate-100 px-1 rounded">{b.code}</span>}
                          {b.owner && <span className="ml-2 text-xs text-slate-400">{b.owner}</span>}
                        </span>
                      </td>
                      <Cells r={b} />
                    </tr>
                    {open[b.id] && b.people.map(p => (
                      <tr key={p.person_id} className="bg-slate-50/60 text-slate-600" data-testid={`ach-person-${b.id}-${p.person_id}`}>
                        <td className="pl-12 pr-3 py-1.5">{p.name}</td>
                        <Cells r={p} />
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4 overflow-x-auto" data-testid="salesperson-targets">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Salesperson targets</h3>
          <table className="w-full text-sm min-w-[520px]">
            <thead><tr className="text-xs text-slate-500 uppercase">{['Salesperson', 'Target', 'Won', 'Achievement', 'Pipeline', 'Gap'].map((h, i) => <th key={h} className={`px-3 py-1.5 ${i ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.salespeople.filter(p => p.active || p.target).map(p => <tr key={p.id} data-testid={`sp-target-row-${p.id}`}><td className="px-3 py-2 font-medium">{p.name}</td><Cells r={p} /></tr>)}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="kpi-targets">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Company strategic KPIs</h3>
          <div className="divide-y divide-slate-100">
            {data.kpis.length === 0 && <p className="text-sm text-slate-400 py-4">No KPIs set</p>}
            {data.kpis.map((k, i) => (
              <div key={i} className="flex items-start justify-between gap-3 py-2">
                <div><p className="text-sm font-medium text-slate-800">{k.name}</p><p className="text-xs text-slate-400">{k.focus}</p></div>
                <div className="text-right shrink-0"><p className="text-sm font-semibold tabular-nums">{fmtKpi(k)}</p>{fmtCur(k) != null ? <p className="text-xs text-green-700" data-testid={`kpi-current-${i}`}>Now {fmtCur(k)} ({k.current_pct}%)</p> : <p className="text-xs text-slate-400">{k.unit === 'amount' ? '' : k.basis}</p>}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
